import { useState } from "react";
import { Modal } from "../../Modal";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { adminFetch } from "../../../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, X } from "lucide-react";

interface Category {
  id: string;
  name_en: string;
  name_tr: string;
}

interface CategoryManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  onRefresh: () => void;
}

function CategoryForm({
  id, setId, nameEn, setNameEn, nameTr, setNameTr,
  editingCat, cancelEdit, onSubmit
}: any) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 p-1 border-b border-border/60 pb-5">
      <h4 className="text-xs font-bold text-brand-primary uppercase tracking-wider">
        {editingCat ? "Edit Category" : "Create New Category"}
      </h4>
      <div className="grid grid-cols-3 gap-3">
        {!editingCat && (
          <div className="space-y-1">
            <Label htmlFor="cat-id">ID / Code</Label>
            <Input id="cat-id" placeholder="tech" value={id} onChange={(e) => setId(e.target.value)} required />
          </div>
        )}
        <div className={editingCat ? "col-span-2 space-y-1" : "space-y-1"}>
          <Label htmlFor="cat-name-en">English Name</Label>
          <Input id="cat-name-en" placeholder="Technology" value={nameEn} onChange={(e) => setNameEn(e.target.value)} required />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cat-name-tr">Turkish Name</Label>
          <Input id="cat-name-tr" placeholder="Teknoloji" value={nameTr} onChange={(e) => setNameTr(e.target.value)} required />
        </div>
      </div>
      <div className="flex justify-end gap-2 text-xs">
        {editingCat && (
          <Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
            <X className="h-3.5 w-3.5 mr-1" /> Cancel
          </Button>
        )}
        <Button type="submit" size="sm" className="bg-brand-primary text-white">
          {editingCat ? <><Check className="h-3.5 w-3.5 mr-1" /> Update</> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add Category</>}
        </Button>
      </div>
    </form>
  );
}

function CategoryList({
  categories,
  onEdit,
  onDelete,
}: {
  categories: Category[];
  onEdit: (cat: Category) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="space-y-2 pt-3 max-h-56 overflow-y-auto">
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Existing Categories</h4>
      {categories.length === 0 ? (
        <p className="text-xs italic text-muted-foreground py-4 text-center">No categories defined yet.</p>
      ) : (
        categories.map((cat) => (
          <div key={cat.id} className="flex items-center justify-between p-2.5 border border-border/40 rounded-xl bg-muted/10 text-xs">
            <div>
              <span className="font-bold text-brand-primary font-mono select-all">[{cat.id}]</span>
              <span className="ml-2 font-medium text-foreground">{cat.name_en}</span>
              <span className="text-muted-foreground mx-1.5">|</span>
              <span className="font-medium text-foreground">{cat.name_tr}</span>
            </div>
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon" onClick={() => onEdit(cat)} className="h-7 w-7 text-brand-secondary">
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button type="button" variant="ghost" size="icon" onClick={() => onDelete(cat.id)} className="h-7 w-7 text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export function CategoryManagerModal({
  isOpen,
  onClose,
  categories,
  onRefresh,
}: CategoryManagerModalProps) {
  const [id, setId] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameTr, setNameTr] = useState("");
  const [editingCat, setEditingCat] = useState<Category | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const catId = editingCat ? editingCat.id : id.trim().toLowerCase().replace(/[^a-z0-9-_]/g, "-");
    const en = nameEn.trim();
    const tr = nameTr.trim();

    if (!catId || !en || !tr) {
      toast.error("Category ID and both translation names are required.");
      return;
    }

    try {
      await adminFetch("/api/admin/blog-categories", { id: catId, name_en: en, name_tr: tr });
      toast.success(editingCat ? "Category updated successfully!" : "Category added successfully!");
      setId("");
      setNameEn("");
      setNameTr("");
      setEditingCat(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save category");
    }
  };

  const handleDelete = async (catId: string) => {
    if (!confirm("Are you sure you want to delete this category? Any blogs with this category will lose the association.")) return;
    try {
      await adminFetch("/api/admin/blog-categories", { operation: "delete", id: catId });
      toast.success("Category deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete category");
    }
  };

  const startEdit = (cat: Category) => {
    setEditingCat(cat);
    setNameEn(cat.name_en);
    setNameTr(cat.name_tr);
  };

  const cancelEdit = () => {
    setEditingCat(null);
    setNameEn("");
    setNameTr("");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Blog Categories" subtitle="Configure categories used in blog articles">
      <CategoryForm
        id={id} setId={setId} nameEn={nameEn} setNameEn={setNameEn} nameTr={nameTr} setNameTr={setNameTr}
        editingCat={editingCat} cancelEdit={cancelEdit} onSubmit={handleSave}
      />
      <CategoryList categories={categories} onEdit={startEdit} onDelete={handleDelete} />
    </Modal>
  );
}
export default CategoryManagerModal;
